import type { AgencyGroupRef } from '@gat-ship/types'
import type { Ref } from 'vue'
import { useAgencyApiCalls } from '@gat-ship/lib/http-client'
import { useAgencySignalREvents } from '../../signalr/events/agency-signalR-events'

export function useAgencyGroupRefDataSource(
  agencyGroupsRef: Ref<AgencyGroupRef[] | undefined>
) {
  const { getAgenyGroupRefsRequest } = useAgencyApiCalls()

  /**
   * The agency groups.
   */

  function setData(data: AgencyGroupRef[] | undefined) {
    agencyGroupsRef.value = data
  }

  function addAgencyGroup(agencyGroup: AgencyGroupRef) {
    const result = [...(agencyGroupsRef.value ?? []), agencyGroup]
    setData(result)
  }

  function updateAgencyGroup(
    agencyGroupId: string,
    agencyGroup: AgencyGroupRef
  ) {
    const result = agencyGroupsRef.value?.map((i) =>
      i.id === agencyGroupId ? agencyGroup : i
    )
    setData(result)
  }

  const agencySignalREvents = useAgencySignalREvents({
    onAgencyGroupAdded: () => {
      fetchAgencyGroups({ forceFetch: true })
    },
    onAgencyGroupDeactivated: () => {
      fetchAgencyGroups({ forceFetch: true })
    },
    onAgencyGroupMoved: () => {
      fetchAgencyGroups({ forceFetch: true })
    },
    onAgencyGroupNameChanged: () => {
      fetchAgencyGroups({ forceFetch: true })
    }
  })

  function unsubscribeFromEvents() {
    agencySignalREvents.unsubscribeFromAgencyEvents()
  }

  function subscribeToEvents() {
    agencySignalREvents.subscribeToAgencyEvents()
    return () => {
      unsubscribeFromEvents()
    }
  }

  /**
   * Fetches the agency groups.
   * @param options.forceFetch - Whether to force a fetch. @default false
   * @param options.subscribeToEvents - Whether to subscribe to signalR events. @default true
   */
  async function fetchAgencyGroups(options?: {
    forceFetch?: boolean
    subscribeToEvents?: boolean
  }) {
    if (agencyGroupsRef.value === undefined || (options?.forceFetch ?? false)) {
      const result = await getAgenyGroupRefsRequest()
      setData(result)
    }
    if (options?.subscribeToEvents ?? true) {
      subscribeToEvents()
    }
  }

  return {
    agencyGroups: agencyGroupsRef,
    /**
     * Fetches the agency groups.
     * @param options.forceFetch - Whether to force a fetch. @default false
     * @param options.subscribeToEvents - Whether to subscribe to signalR events. @default true
     */
    fetchAgencyGroups,
    /**
     * Adds an agency group.
     * @param agencyGroup - The agency group to add.
     */
    addAgencyGroup,
    /**
     * Updates an agency group.
     * @param agencyGroupId - The ID of the agency group to update.
     * @param agencyGroup - The agency group to update.
     */
    updateAgencyGroup,
    /**
     * Clears the store and unsubscribes from agency events.
     * @param options.unsubscribeFromEvents - Whether to unsubscribe from signalR events. @default true
     */
    clearData: (options?: { unsubscribeFromEvents?: boolean }) => {
      setData([])
      if (options?.unsubscribeFromEvents ?? true) {
        unsubscribeFromEvents()
      }
    },
    /**
     * Subscribes to signalR events for agency groups.
     */
    subscribeToEvents,
    /**
     * Unsubscribes from signalR events for agency groups.
     */
    unsubscribeFromEvents
  }
}
